import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'next-i18next';
import * as z from 'zod';

import { StoreCategoryString } from '../consts';
import {
  StoreAppUploadDataType,
  StoreAssetUploadDataType,
  StoreItemType,
  StoreUploadCommonDataType,
  StoreUploadLocalizeData,
} from '../types/StoreTypes';

type ZodRuleType<T> = { [key in keyof T]: z.ZodTypeAny };

/**
 * 공통 Validation 체크를 위한 zod 객체
 * @param trans
 * @param uploadType
 */
function useMakeCommonUploadSchema(uploadType: StoreItemType) {
  const { t } = useTranslation(['store']);
  return z.object<ZodRuleType<StoreUploadCommonDataType>>({
    objectImageKeyList: z
      .array(z.any())
      .min(
        uploadType === StoreCategoryString.object ? 1 : 0,
        t('store:store_upload_object_image_required'),
      )
      .max(100, t('store:store_alert_max_registration_num', { count: 100 })),
    isFree: z.boolean(),
    price: z.number().min(0),
  });
}

/**
 * 맵/오브젝트 등록/수정을 위한 공통 리랙트 폼 객체 생성
 * @param uploadType
 * @param trans
 * @param defaultValues
 */
export const useStoreAssetForm = (
  uploadType: StoreItemType,
  defaultValues?: StoreAssetUploadDataType,
): UseFormReturn<StoreAssetUploadDataType> => {
  const { t } = useTranslation(['store']);
  const commonScheme = useMakeCommonUploadSchema(uploadType);
  const storeAssetUploadSchema = z
    .object<ZodRuleType<StoreAssetUploadDataType>>({
      sourceSpaceHashId: z
        .string()
        .min(
          uploadType === StoreCategoryString.map ? 1 : 0,
          t('store:store_select_map_required'),
        ),
    })
    .merge(commonScheme)
    .superRefine((value, context) => {
      if (!value.isFree && !value.price) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('store:store_enter_required'),
        });
      }
      if (uploadType === StoreCategoryString.map && !value.sourceSpaceHashId) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('store:store_select_map_required'),
        });
      }
    });

  return useForm<StoreAssetUploadDataType>({
    resolver: zodResolver(storeAssetUploadSchema, undefined, { mode: 'sync' }),
    mode: 'onChange',
    defaultValues,
  });
};

/**
 * 앱/미니 게임 등록을 위한 공통 리랙트 폼 객체 생성
 * @param uploadType
 * @param trans
 * @param defaultValues
 */
export const useStoreAppForm = (
  uploadType: StoreItemType,
  defaultValues?: StoreAppUploadDataType,
) => {
  const { t } = useTranslation(['store']);
  const commonScheme = useMakeCommonUploadSchema(uploadType);
  const storeAppUploadSchema = z
    .object<ZodRuleType<StoreAppUploadDataType>>({
      subCategory: z
        .number()
        .min(
          uploadType === StoreCategoryString.app ? 1 : 0,
          t('store:store_enter_required'),
        ),
      iconImageKey: z.string().min(1, t('store:store_enter_required')),
      sourceAppHashId: z.string().min(1, t('store:store_enter_required')),
    })
    .merge(commonScheme)
    .superRefine((value, context) => {
      if (!value.isFree && !value.price) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('store:store_enter_required'),
        });
      }
      if (
        uploadType === 'app' &&
        !value.subCategory &&
        value.subCategory !== 0
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('store:store_enter_required'),
        });
      }
    });

  return useForm<StoreAppUploadDataType>({
    resolver: zodResolver(storeAppUploadSchema, undefined, { mode: 'sync' }),
    mode: 'onChange',
    defaultValues,
  });
};

/**
 * 언어별 상세 정보를 위한 리액토 폼 객체 생성
 * @param uploadType
 * @param trans
 * @param defaultValues
 */
export const useStoreLocalizeSetForm = (
  uploadType: string,
  defaultValues?: StoreUploadLocalizeData,
) => {
  const { t } = useTranslation(['store']);
  const storeUploadSchema = z.object<ZodRuleType<StoreUploadLocalizeData>>({
    name: z
      .string()
      .min(1, t('store:store_enter_required'))
      .max(30, t('store:store_enter_maximum_char_30')),
    shortDescription: z
      .string()
      .min(
        uploadType === StoreCategoryString.map ||
          uploadType === StoreCategoryString.object
          ? 0
          : 1,
        t('store:store_enter_required'),
      )
      .max(100, t('store:store_enter_maximum_char_100')),
    previewImageKeyList: z
      .array(z.any())
      .min(
        uploadType === StoreCategoryString.object ? 0 : 1,
        t('store:store_enter_required'),
      )
      .nullable(),
    thumbnailImageKey:
      uploadType === StoreCategoryString.map ||
      uploadType === StoreCategoryString.object
        ? z.string().min(1, t('store:store_enter_required')).nullable()
        : z.string().nullable(),
  });

  return useForm<StoreUploadLocalizeData>({
    resolver: zodResolver(storeUploadSchema, undefined, { mode: 'sync' }),
    mode: 'onChange',
    defaultValues,
  });
};
